import { useEffect, useMemo, useRef, useState } from 'react';

export default function MulnyangGameHud({
  durationSeconds,
  wrongClickCount,
  clickLimit,
  resetKey,
  isPaused = false,
  onTimeUp,
}) {
  const safeDurationSeconds = Number.isFinite(Number(durationSeconds)) ? Number(durationSeconds) : 0;
  const [remainingMs, setRemainingMs] = useState(Math.max(0, safeDurationSeconds * 1000));
  const audioCtxRef = useRef(null);
  const lastTickSecondRef = useRef(null);
  const timeUpTriggeredRef = useRef(false);
  const remainingMsRef = useRef(Math.max(0, safeDurationSeconds * 1000));

  useEffect(() => {
    const nextRemaining = Math.max(0, safeDurationSeconds * 1000);
    setRemainingMs(nextRemaining);
    remainingMsRef.current = nextRemaining;
    lastTickSecondRef.current = null;
    timeUpTriggeredRef.current = false;
  }, [safeDurationSeconds, resetKey]);

  useEffect(() => {
    if (isPaused || remainingMsRef.current <= 0) return undefined;

    let lastNow = performance.now();
    let frameId = 0;

    const tick = (now) => {
      const elapsed = now - lastNow;
      lastNow = now;

      const nextRemaining = Math.max(0, remainingMsRef.current - elapsed);
      remainingMsRef.current = nextRemaining;
      setRemainingMs(nextRemaining);
      if (nextRemaining > 0) {
        frameId = window.requestAnimationFrame(tick);
      }
    };

    frameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [safeDurationSeconds, resetKey, isPaused]);

  const secondsLeft = useMemo(() => Math.max(0, Math.ceil(remainingMs / 1000)), [remainingMs]);

  useEffect(() => {
    if (isPaused || timeUpTriggeredRef.current) return;
    if (secondsLeft !== 0) return;
    const handled = onTimeUp?.();
    if (handled !== false) {
      timeUpTriggeredRef.current = true;
    }
  }, [secondsLeft, isPaused, onTimeUp]);

  useEffect(() => {
    const playTick = () => {
      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = audioCtxRef.current;
        if (ctx.state === 'suspended') ctx.resume();

        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(1200, ctx.currentTime);
        gain1.gain.setValueAtTime(0.08, ctx.currentTime);
        gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
        osc1.connect(gain1);
        gain1.connect(ctx.destination);
        osc1.start(ctx.currentTime);
        osc1.stop(ctx.currentTime + 0.08);

        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(900, ctx.currentTime + 0.08);
        gain2.gain.setValueAtTime(0.06, ctx.currentTime + 0.08);
        gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.14);
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.start(ctx.currentTime + 0.08);
        osc2.stop(ctx.currentTime + 0.15);
      } catch (error) {
        // Ignore audio failures (autoplay restrictions, unsupported environment).
      }
    };

    if (secondsLeft > 0 && secondsLeft <= 3 && lastTickSecondRef.current !== secondsLeft) {
      playTick();
      lastTickSecondRef.current = secondsLeft;
    }

    if (secondsLeft > 3 || secondsLeft <= 0) {
      lastTickSecondRef.current = null;
    }
  }, [secondsLeft]);

  useEffect(() => {
    return () => {
      if (audioCtxRef.current && typeof audioCtxRef.current.close === 'function') {
        audioCtxRef.current.close().catch(() => {});
      }
      audioCtxRef.current = null;
    };
  }, []);

  const gaugePercent = useMemo(() => {
    const durationMs = safeDurationSeconds * 1000;
    if (!durationMs || durationMs <= 0) return 0;
    return Math.max(0, Math.min(100, (remainingMs / durationMs) * 100));
  }, [safeDurationSeconds, remainingMs]);

  const isTimeDanger = remainingMs > 0 && remainingMs <= 3000;
  const isClickDanger = wrongClickCount >= 5;
  const blinkDurationMs = useMemo(() => {
    if (!isTimeDanger) return 0;
    const minMs = 220;
    const maxMs = 820;
    const ratio = Math.max(0, Math.min(1, remainingMs / 3000));
    return Math.round(minMs + (maxMs - minMs) * ratio);
  }, [isTimeDanger, remainingMs]);

  return (
    <div className="mulnyang-game-hud">
      <div className="mulnyang-time-gauge">
        <div className="mulnyang-time-track">
          <div
            className={`mulnyang-time-fill${isTimeDanger ? ' danger blinking' : ''}`}
            style={{
              transform: `scaleX(${gaugePercent / 100})`,
              animationDuration: isTimeDanger ? `${blinkDurationMs}ms` : undefined,
            }}
          />
        </div>
        <div className="mulnyang-time-pill">
          <span className="mulnyang-time-pill-text">{`${String(secondsLeft).padStart(2, '0')}s`}</span>
        </div>
      </div>

      <div className={`mulnyang-click-pill${isClickDanger ? ' is-danger' : ''}`}>
        <span className="current">{String(wrongClickCount).padStart(2, '0')}</span>
        <span className="slash">/</span>
        <span className="max">{String(clickLimit).padStart(2, '0')}</span>
      </div>
    </div>
  );
}
